import React, { useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { formatMoney } from '../../api/bookingApi';
import { useBooking } from '../../booking/BookingContext';
import { isValidIntlPhone, normalizeIntlPhone } from '../../booking/phone';
import IntlPhoneField from '../../components/IntlPhoneField';

function pointLabel(p) {
  return (p?.name || '').toString();
}

function samePoint(a, b) {
  if (!a || !b) return false;
  const aid = (a.id || '').toString();
  const bid = (b.id || '').toString();
  if (aid && bid) return aid === bid;
  return pointLabel(a) === pointLabel(b);
}

function ChoiceChip({ selected, label, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex-1 py-3 rounded-lg text-sm font-bold border ${
        selected
          ? 'bg-white border-superfeo-pink text-superfeo-pink'
          : 'bg-white/70 border-pink-200 text-gray-500'
      }`}
    >
      {label}
    </button>
  );
}

const fieldClass =
  'w-full rounded-lg border border-pink-200 bg-white px-3 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-superfeo-pink/30 focus:border-superfeo-pink';

export default function DetailsPage() {
  const booking = useBooking();
  const navigate = useNavigate();
  const trip = booking.selectedTrip;

  const initialRows = useMemo(
    () =>
      booking.selectedSeats.map((seat) => ({
        seat,
        firstName: '',
        lastName: '',
        ageGroup: 'adult',
        gender: 'M',
      })),
    [booking.selectedSeats]
  );

  const [rows, setRows] = useState(initialRows);
  const [phone, setPhone] = useState(() => booking.contactPhone || '');
  const [email, setEmail] = useState(booking.contactEmail || '');
  const [pickup, setPickup] = useState(booking.pickupPoint);
  const [dropoff, setDropoff] = useState(booking.dropoffPoint);
  const [emergencyOpen, setEmergencyOpen] = useState(
    Boolean(
      booking.emergencyName ||
        booking.emergencyPhone ||
        booking.emergencyEmail
    )
  );
  const [emergencyName, setEmergencyName] = useState(
    booking.emergencyName || ''
  );
  const [emergencyPhone, setEmergencyPhone] = useState(
    () => booking.emergencyPhone || ''
  );
  const [emergencyEmail, setEmergencyEmail] = useState(
    booking.emergencyEmail || ''
  );
  const [error, setError] = useState('');

  if (!trip) return <Navigate to="/book/trips" replace />;
  if (!booking.selectedSeats.length) {
    return <Navigate to="/book/seats" replace />;
  }

  const pickups = trip.pickupPoints || [];
  const dropoffs = trip.dropoffPoints || [];

  const updateRow = (index, patch) => {
    setRows((prev) =>
      prev.map((r, i) => (i === index ? { ...r, ...patch } : r))
    );
  };

  const submit = (e) => {
    e.preventDefault();
    setError('');

    if (rows.some((r) => !r.firstName.trim() || !r.lastName.trim())) {
      setError('Enter first and last name for each passenger.');
      return;
    }
    if (!isValidIntlPhone(phone)) {
      setError('Enter a valid phone number with country code.');
      return;
    }
    if (pickups.length > 1 && !pickup) {
      setError('Select a boarding point.');
      return;
    }
    if (dropoffs.length > 1 && !dropoff) {
      setError('Select a drop-off point.');
      return;
    }

    const eName = emergencyName.trim();
    const eEmail = emergencyEmail.trim();
    const ePhoneRaw = emergencyPhone.trim();
    const eFilled = Boolean(eName || ePhoneRaw || eEmail);
    let ePhone = '';
    if (eFilled) {
      if (!eName || !isValidIntlPhone(ePhoneRaw)) {
        setError(
          'Enter emergency contact name and a valid phone, or leave all fields empty.'
        );
        return;
      }
      ePhone = normalizeIntlPhone(ePhoneRaw);
    }

    const e164 = normalizeIntlPhone(phone);
    booking.setContact({ phone: e164, email: email.trim() });
    booking.setEmergencyContact({
      name: eName,
      phone: ePhone,
      email: eEmail,
    });
    booking.setStops({ pickup, dropoff });
    booking.setPassengerDetails(
      rows.map((r) => ({
        firstName: r.firstName.trim(),
        lastName: r.lastName.trim(),
        name: `${r.firstName.trim()} ${r.lastName.trim()}`.trim(),
        phone: e164,
        gender: r.gender,
        sex: r.gender,
        ageGroup: r.ageGroup,
        seat: r.seat,
      }))
    );
    navigate('/book/payment');
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      {error && (
        <div className="bg-red-50 border border-red-100 text-red-700 text-sm rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      {(pickups.length > 0 || dropoffs.length > 0) && (
        <div className="rounded-2xl border border-pink-200 bg-pink-50 p-4 shadow-sm">
          <div className="flex flex-col sm:flex-row gap-3">
            {pickups.length > 0 && (
              <label className="flex-1 text-sm font-medium text-gray-700">
                Boarding point
                <select
                  className={`${fieldClass} mt-1.5`}
                  value={
                    pickups.findIndex((p) => samePoint(p, pickup)) >= 0
                      ? String(
                          pickups.findIndex((p) => samePoint(p, pickup))
                        )
                      : ''
                  }
                  onChange={(ev) => {
                    const i = Number(ev.target.value);
                    setPickup(
                      Number.isFinite(i) && pickups[i] ? { ...pickups[i] } : null
                    );
                  }}
                >
                  <option value="">Select…</option>
                  {pickups.map((p, i) => (
                    <option key={`${pointLabel(p)}-${i}`} value={String(i)}>
                      {pointLabel(p)}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {dropoffs.length > 0 && (
              <label className="flex-1 text-sm font-medium text-gray-700">
                Drop-off point
                <select
                  className={`${fieldClass} mt-1.5`}
                  value={
                    dropoffs.findIndex((p) => samePoint(p, dropoff)) >= 0
                      ? String(
                          dropoffs.findIndex((p) => samePoint(p, dropoff))
                        )
                      : ''
                  }
                  onChange={(ev) => {
                    const i = Number(ev.target.value);
                    setDropoff(
                      Number.isFinite(i) && dropoffs[i]
                        ? { ...dropoffs[i] }
                        : null
                    );
                  }}
                >
                  <option value="">Select…</option>
                  {dropoffs.map((p, i) => (
                    <option key={`${pointLabel(p)}-${i}`} value={String(i)}>
                      {pointLabel(p)}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </div>
        </div>
      )}

      {rows.map((row, i) => (
        <div
          key={row.seat}
          className="rounded-2xl border border-pink-200 bg-pink-50 p-4 shadow-sm space-y-4"
        >
          <div className="flex items-center gap-2">
            <span className="bg-superfeo-pink text-white text-[11px] font-bold px-2 py-1 rounded-lg">
              Seat {row.seat}
            </span>
            <span className="text-sm text-gray-500">Passenger {i + 1}</span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="text-sm font-medium text-gray-700">
              First name
              <input
                className={`${fieldClass} mt-1.5`}
                value={row.firstName}
                onChange={(e) =>
                  updateRow(i, { firstName: e.target.value })
                }
                autoComplete="given-name"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Last name
              <input
                className={`${fieldClass} mt-1.5`}
                value={row.lastName}
                onChange={(e) => updateRow(i, { lastName: e.target.value })}
                autoComplete="family-name"
              />
            </label>
          </div>

          {i === 0 && (
            <>
              <label className="block text-sm font-medium text-gray-700">
                Mobile number
                <div className="mt-1.5">
                  <IntlPhoneField
                    value={phone}
                    onChangeE164={setPhone}
                    autoComplete="tel"
                    required
                  />
                </div>
              </label>
              <label className="block text-sm font-medium text-gray-700">
                Email (optional)
                <input
                  className={`${fieldClass} mt-1.5`}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  type="email"
                  autoComplete="email"
                />
              </label>
            </>
          )}

          <div>
            <p className="text-xs font-bold text-gray-500 mb-2">Age group</p>
            <div className="flex gap-2">
              <ChoiceChip
                selected={row.ageGroup === 'adult'}
                label="Adult"
                onClick={() => updateRow(i, { ageGroup: 'adult' })}
              />
              <ChoiceChip
                selected={row.ageGroup === 'child'}
                label="Child"
                onClick={() => updateRow(i, { ageGroup: 'child' })}
              />
            </div>
          </div>

          <div>
            <p className="text-xs font-bold text-gray-500 mb-2">Gender</p>
            <div className="flex gap-2">
              <ChoiceChip
                selected={row.gender === 'M'}
                label="Male"
                onClick={() => updateRow(i, { gender: 'M' })}
              />
              <ChoiceChip
                selected={row.gender === 'F'}
                label="Female"
                onClick={() => updateRow(i, { gender: 'F' })}
              />
            </div>
          </div>

          {i === rows.length - 1 && (
            <div className="pt-2">
              <button
                type="button"
                onClick={() => setEmergencyOpen((v) => !v)}
                className="w-full flex items-center gap-2 bg-white/70 border border-pink-200 rounded-lg px-3 py-3 text-left"
              >
                <span className="text-superfeo-pink text-lg">+</span>
                <span className="flex-1 text-sm font-bold text-gray-800">
                  Emergency contact
                </span>
                <span className="text-[10px] font-bold text-superfeo-darkpink bg-pink-50 border border-pink-200 rounded-full px-2 py-0.5">
                  Optional
                </span>
                <span className="text-gray-400">
                  {emergencyOpen ? '▴' : '▾'}
                </span>
              </button>
              {emergencyOpen && (
                <div className="mt-3 space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Full name
                    <input
                      className={`${fieldClass} mt-1.5`}
                      value={emergencyName}
                      onChange={(e) => setEmergencyName(e.target.value)}
                    />
                  </label>
                  <label className="block text-sm font-medium text-gray-700">
                    Phone number
                    <div className="mt-1.5">
                      <IntlPhoneField
                        value={emergencyPhone}
                        onChangeE164={setEmergencyPhone}
                      />
                    </div>
                  </label>
                  <label className="block text-sm font-medium text-gray-700">
                    Email (optional)
                    <input
                      className={`${fieldClass} mt-1.5`}
                      value={emergencyEmail}
                      onChange={(e) => setEmergencyEmail(e.target.value)}
                      type="email"
                    />
                  </label>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      <div className="rounded-2xl border border-pink-200 bg-pink-50 p-4 shadow-sm">
        <div className="flex justify-between items-center mb-4">
          <span className="text-sm font-semibold text-superfeo-darkpink">
            Total
          </span>
          <span className="font-heading font-bold text-xl text-superfeo-darkpink">
            {formatMoney(booking.total, trip.currency || 'TZS')}
          </span>
        </div>
        <button
          type="submit"
          className="w-full bg-superfeo-pink hover:bg-superfeo-darkpink text-white font-bold py-3.5 rounded-xl"
        >
          Continue to payment
        </button>
      </div>
    </form>
  );
}
